import { useMemo } from "react";
import { formatQuoteNumber } from "@forgepoint/shared";
import { formatAddressLines } from "@/modules/publicQuote/utils";
import { useOrganizationStore } from "@/store/organizationStore";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import type { ConfiguredSampleValues } from "./sampleQuote";

/**
 * The sequence a sample quote number is formatted from, matching the invented
 * default so a configured prefix and padding change only the format.
 */
const SAMPLE_QUOTE_SEQUENCE = 42;

/**
 * The sample defaults the organization has already configured, so sample mode
 * shows the org's own terms, footer, letterhead, and quote numbering rather
 * than invented text. Only the values a newly created quote would genuinely
 * carry are mapped: everything else on a quote is per-quote data with no
 * org-wide counterpart, and stays invented.
 *
 * Both stores are already loaded wherever the template editor opens (the
 * organization by the app layout, the quote settings by the panel the editor is
 * launched from), so this reads them without triggering a fetch.
 * @returns The configured values, keyed by sample key
 */
export function useConfiguredSampleValues(): ConfiguredSampleValues {
  const organization = useOrganizationStore((state) => state.organization);
  const settings = useQuoteSettingsStore((state) => state.settings);

  return useMemo(
    () => ({
      "org-name": organization?.name ?? null,
      "org-address":
        formatAddressLines(organization?.address)?.join(", ") ?? null,
      "org-website": organization?.website ?? null,
      "org-phone": organization?.phone ?? null,
      "org-fax": organization?.fax ?? null,
      "quote-number": configuredQuoteNumber(
        settings?.quoteNumberPadWidth ?? null,
        settings?.quoteNumberPrefix ?? null,
      ),
      "quote-footer": settings?.quoteFooter ?? null,
      "payment-terms": settings?.paymentTerms ?? null,
      "delivery-terms": settings?.deliveryTerms ?? null,
      "terms-of-service": settings?.termsAndConditions ?? null,
    }),
    [organization, settings],
  );
}

/**
 * A sample quote number in the organization's own format, or null when it has
 * configured neither a prefix nor a pad width - a bare "42" reads worse than
 * the invented default it would replace.
 * @param padWidth - The configured zero-padding width, or null
 * @param prefix - The configured quote number prefix, or null
 * @returns The formatted sample quote number, or null when nothing is configured
 */
function configuredQuoteNumber(
  padWidth: number | null,
  prefix: string | null,
): string | null {
  if (!padWidth && !prefix) {
    return null;
  }
  return formatQuoteNumber(SAMPLE_QUOTE_SEQUENCE, padWidth, prefix);
}
